import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { REPORT_FILE_ACTION_TYPE } from '../../../../types';
import { t } from 'i18next';
import utils, { toggleValueInArray } from 'utils';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import { Button, Checkbox } from 'ncoded-component-library';
import RefreshIcon from 'icons/Refresh.icon';
import useQueryFilter from 'hooks/useQueryFilter';
import { NAVIGATION_VARIANTS, NavigationVariants } from 'models/Navigation';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

import './ActionFilter.styles.scss';
import './ActionFilter.styles.responsive.scss';

type ActionFilterProps = {
  className?: string;
  variant?: NavigationVariants;
};

const FILTER_KEY = 'action';

const ActionFilter: React.FC<ActionFilterProps> = (props) => {
  const { className, variant = NAVIGATION_VARIANTS.SECONDARY } = props;

  const createSelectOptions = useCreateSelectOptions();

  const actionOptions = useMemo(
    () => createSelectOptions(Object.values(REPORT_FILE_ACTION_TYPE)),
    [createSelectOptions],
  );

  const {
    params: { action },
    onFiltersApply,
  } = useQueryFilter();

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const defaultVal = useMemo(
    () => (action ? utils.mapParamsToArray(action) : []),
    [action],
  );
  const [currentState, setCurrentState] = useState(defaultVal);

  const classes = classNames('action-filter', className);

  const onSelectFilters = useCallback(
    (options: string[]) => {
      dropdown.setIsOpen(false);

      const patchedVal = utils.multiSelectValueToStringArray(options as any);

      onFiltersApply(FILTER_KEY, patchedVal);
    },
    [dropdown, onFiltersApply],
  );

  const triggerTitle = useMemo(
    () =>
      action
        ? defaultVal?.length < 2
          ? t(utils.stringifyFilterValues(action))
          : t('openToSeeActionFilters')
        : t('action'),
    [defaultVal, action],
  );

  useEffect(() => {
    setCurrentState(defaultVal);
  }, [defaultVal]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      trigerTitle={triggerTitle}
      icon={RefreshIcon}
      variant={variant}
      onClose={() => {
        setCurrentState(defaultVal);
      }}
    >
      <div>
        {actionOptions.map(({ value, label }) => (
          <Checkbox
            key={value}
            label={t(label)}
            checked={currentState.some((el) => el === value)}
            onChange={() => {
              setCurrentState((prev) => toggleValueInArray(prev, value));
            }}
          />
        ))}
      </div>

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={currentState === defaultVal}
          onClick={() => onSelectFilters(currentState)}
        >
          {t('applyActionFilter')}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default ActionFilter;
